import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SessionsTab = () => {
  const [sessions] = useState([
    {
      id: 1,
      device: "MacBook Pro 16-inch",
      browser: "Chrome 119.0",
      os: "macOS 14.1",
      location: "San Francisco, CA",
      ipAddress: "192.168.1.100",
      loginTime: "2025-09-03T14:30:00Z",
      lastActivity: "2025-09-03T15:00:00Z",
      isCurrent: true,
      suspicious: false
    },
    {
      id: 2,
      device: "iPhone 15 Pro",
      browser: "Safari Mobile",
      os: "iOS 17.1",
      location: "San Francisco, CA",
      ipAddress: "192.168.1.101",
      loginTime: "2025-09-03T12:15:00Z",
      lastActivity: "2025-09-03T14:45:00Z",
      isCurrent: false,
      suspicious: false
    },
    {
      id: 3,
      device: "Windows Desktop",
      browser: "Edge 119.0",
      os: "Windows 11",
      location: "New York, NY",
      ipAddress: "203.0.113.45",
      loginTime: "2025-09-02T09:45:00Z",
      lastActivity: "2025-09-02T16:30:00Z",
      isCurrent: false,
      suspicious: true
    }
  ]);

  const [suspiciousActivity] = useState([
    {
      id: 1,
      type: "unusual_location",
      description: "Login from unusual location: Moscow, Russia",
      timestamp: "2025-09-01T08:30:00Z",
      ipAddress: "185.220.101.45",
      action: "blocked",
      severity: "high"
    },
    {
      id: 2,
      type: "multiple_failed_attempts",
      description: "5 failed login attempts in 10 minutes",
      timestamp: "2025-08-30T14:20:00Z",
      ipAddress: "203.0.113.67",
      action: "rate_limited",
      severity: "medium"
    },
    {
      id: 3,
      type: "new_device",
      description: "Login from new device: Android Phone",
      timestamp: "2025-08-28T19:15:00Z",
      ipAddress: "192.168.1.105",
      action: "allowed_after_2fa",
      severity: "low"
    }
  ]);

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeSince = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handleTerminateSession = (sessionId) => {
    console.log('Terminating session:', sessionId);
    // In a real app, this would terminate the session
  };

  const handleTerminateAllSessions = () => {
    if (window.confirm('This will log you out of all devices except this one. Continue?')) {
      console.log('Terminating all other sessions...');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'high': return 'bg-error/10 border-error/20';
      case 'medium': return 'bg-warning/10 border-warning/20';
      case 'low': return 'bg-accent/10 border-accent/20';
      default: return 'bg-muted border-border';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'blocked': return 'text-error';
      case 'rate_limited': return 'text-warning';
      case 'allowed_after_2fa': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-8">
      {/* Active Sessions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="Monitor" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Active Sessions</h3>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleTerminateAllSessions}
            iconName="LogOut"
            iconPosition="left"
          >
            End All Other Sessions
          </Button>
        </div>
        
        <div className="space-y-4">
          {sessions?.map((session) => (
            <div key={session?.id} className={`border rounded-lg p-4 ${
              session?.suspicious ? 'border-warning/50 bg-warning/5' : 'border-border'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    session?.isCurrent ? 'bg-success/10' : session?.suspicious ? 'bg-warning/10' : 'bg-muted'
                  }`}>
                    <Icon 
                      name={session?.device?.includes('iPhone') ? 'Smartphone' : 'Monitor'} 
                      size={20} 
                      className={
                        session?.isCurrent ? 'text-success' : session?.suspicious ? 'text-warning' : 'text-muted-foreground'
                      } 
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-foreground">{session?.device}</h4>
                      {session?.isCurrent && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                          Current Session
                        </span>
                      )}
                      {session?.suspicious && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
                          <Icon name="AlertTriangle" size={12} className="mr-1" />
                          Suspicious
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <p>{session?.browser} • {session?.os}</p>
                      <p className="flex items-center">
                        <Icon name="MapPin" size={14} className="mr-1" />
                        {session?.location} • {session?.ipAddress}
                      </p>
                      <div className="flex items-center space-x-4">
                        <p className="flex items-center">
                          <Icon name="LogIn" size={14} className="mr-1" />
                          Signed in: {formatDateTime(session?.loginTime)}
                        </p>
                        <p className="flex items-center">
                          <Icon name="Clock" size={14} className="mr-1" />
                          Last active: {getTimeSince(session?.lastActivity)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {!session?.isCurrent && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTerminateSession(session?.id)}
                    iconName="X"
                    iconPosition="left"
                  >
                    End Session
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Suspicious Activity */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="AlertTriangle" size={20} className="text-warning" />
          <h3 className="text-lg font-semibold text-foreground">Security Alerts</h3>
        </div>
        
        <div className="space-y-4">
          {suspiciousActivity?.map((activity) => (
            <div key={activity?.id} className={`border rounded-lg p-4 ${getSeverityBg(activity?.severity)}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon 
                      name="AlertTriangle" 
                      size={16} 
                      className={getSeverityColor(activity?.severity)} 
                    />
                    <span className={`text-xs font-medium uppercase tracking-wide ${getSeverityColor(activity?.severity)}`}>
                      {activity?.severity} Risk
                    </span>
                  </div>
                  
                  <h4 className="font-medium text-foreground mb-1">{activity?.description}</h4>
                  
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <p className="flex items-center">
                      <Icon name="Clock" size={14} className="mr-1" />
                      {formatDateTime(activity?.timestamp)}
                    </p>
                    <p className="flex items-center">
                      <Icon name="Globe" size={14} className="mr-1" />
                      {activity?.ipAddress}
                    </p>
                    <p className={`flex items-center font-medium ${getActionColor(activity?.action)}`}>
                      <Icon name="Shield" size={14} className="mr-1" />
                      {activity?.action?.replace('_', ' ')?.toUpperCase()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Session Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Settings" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Session Settings</h3>
        </div>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Session Timeout</h4>
              <p className="text-sm text-muted-foreground">
                Automatically end sessions after 24 hours of inactivity
              </p>
            </div>
            <span className="text-sm text-muted-foreground">24 hours</span>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Concurrent Sessions</h4>
              <p className="text-sm text-muted-foreground">
                Maximum number of active sessions allowed
              </p>
            </div>
            <span className="text-sm text-muted-foreground">5 devices</span>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Location Tracking</h4>
              <p className="text-sm text-muted-foreground">
                Monitor and alert on logins from new locations
              </p>
            </div>
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
              <Icon name="CheckCircle" size={12} className="mr-1" />
              Enabled
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionsTab;